import React from 'react';
import { useForm } from 'react-hook-form';
import { SmallButton } from '@/components/Views/Buttons';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import { submitTeacherInfoCapture } from 'lib/hubspot';
import { submitConnectTrialLead } from 'lib/muzology';
import { useRouter } from 'next/router';

const MIAMI_DADE_EVENT_TITLE = 'Miami-Dade Math Extravaganza';
const MIAMI_DADE_EVENT_CODE = 'miami-dade-math-extravaganza-2026';
// const MIAMI_DADE_EVENT_COPY = 'We\'ll email you about how to start your extended trial';
const MIAMI_DADE_EVENT_COPY = 'Special Extended Trial';
const DEFAULT_JOB_TITLE = 'teacher';
const DEFAULT_SCHOOL_OR_DISTRICT_NAME = 'miami-dade';
const THANK_YOU_MESSAGE = 'Thank you! We\'ll email you with instructions for starting your extended trial.';
const SUBMIT_ERROR_MESSAGE = 'Sorry, something went wrong. Please try submitting the form again.';

const normalizeQueryValue = (value) => {
    if (Array.isArray(value)) return value[0] || null;
    if (typeof value === 'string' && value.trim()) return value.trim();
    return null;
};

const getEventCode = (query) => normalizeQueryValue(query?.code) || normalizeQueryValue(query?.conference) || MIAMI_DADE_EVENT_CODE;

interface SummerSchoolSignUpFormData {
    firstname: string;
    email: string;
    jobtitle: string;
    muz_account_school_name: string;
}

const SummerSchoolSignUp = () => {
    const [loading, setLoading] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    const [submitError, setSubmitError] = React.useState(false);
    const router = useRouter();
    const eventCode = getEventCode(router.query);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<SummerSchoolSignUpFormData>({
        defaultValues: {
            firstname: '',
            email: '',
            jobtitle: DEFAULT_JOB_TITLE,
            'muz_account_school_name': DEFAULT_SCHOOL_OR_DISTRICT_NAME
        }
    });

    const onSubmit = async (data: SummerSchoolSignUpFormData) => {
        setLoading(true);
        setSubmitError(false);

        try {
            await Promise.all([
                submitTeacherInfoCapture(data, `connect extended trial - ${eventCode}`),
                submitConnectTrialLead({
                    ...data,
                    code: eventCode,
                    pageUri: window.location.href
                })
            ]);
            setSubmitted(true);
        } catch (error) {
            console.error('Error submitting connect trial form:', error);
            setSubmitError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <LoadingOverlay show={loading} className='fixed z-50' />
            <div className='summer-school-signup'>
                <div className='relative flex flex-col justify-center overflow-hidden px-4 mt-32'>
                    <div className='w-full max-w-[760px] mx-auto text-center'>
                        <h1 className='m-0 text-DavysGrey text-3xl sm:text-5xl font-black leading-tight'>
                            {MIAMI_DADE_EVENT_TITLE}
                        </h1>
                        <p className='mt-5 mb-0 text-DavysGrey text-lg sm:text-2xl font-semibold leading-snug'>
                            {MIAMI_DADE_EVENT_COPY}
                        </p>
                    </div>
                    <div className='w-full max-w-[410px] mt-4 mx-auto mb-6 py-6 px-7 m-auto bg-white rounded-2xl shadow-md drop-shadow-lg '>
                        {submitted ? (
                            <div className='py-6 text-center'>
                                <h2 className='m-0 text-DavysGrey text-2xl font-black leading-snug'>
                                    {THANK_YOU_MESSAGE}
                                </h2>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit(onSubmit)} className='mt-0 flex flex-col gap-6'>
                                <input type='hidden' {...register('jobtitle', { required: true })} />
                                <input type='hidden' {...register('muz_account_school_name', { required: true })} />
                                <div className=''>
                                    <label className='block text-sm font-semibold text-gray-800'>Full Name</label>
                                    <input
                                        type='input'
                                        autoComplete='name'
                                        {...register('firstname', { required: true })}
                                        className='block w-full px-4 py-2 mt-2 text-[#6B7280] bg-white border-[#D1D5DB] border-[1.5px] border-solid rounded-md focus:border-[#5EC5DF] focus:ring-[#5EC5DF] focus:outline-none focus:ring focus:ring-opacity-40'
                                    />
                                    {errors.firstname && <div className='mt-1 text-red-500'>* This field is required</div>}
                                </div>
                                <div className=''>
                                    <label className='block text-sm font-semibold text-gray-800'>Email</label>
                                    <input
                                        type='email'
                                        autoComplete='email'
                                        {...register('email', { required: true })}
                                        className='block w-full px-4 py-2 mt-2 text-[#6B7280] bg-white border-[#D1D5DB] border-[1.5px] border-solid rounded-md focus:border-[#5EC5DF] focus:ring-[#5EC5DF] focus:outline-none focus:ring focus:ring-opacity-40'
                                    />
                                    {errors.email && errors.email.type === 'required' && <div className='mt-1 text-red-500'>* This field is required</div>}
                                    {errors.email && errors.email.type === 'validate' && (
                                        <div className='mt-1 text-red-500'>* Please use your official work/school email address</div>
                                    )}
                                    {errors.email && errors.email.type === 'manual' && (
                                        <div className='mt-1 text-red-500'>
                                            * <span dangerouslySetInnerHTML={{ __html: errors.email.message as string }} />
                                        </div>
                                    )}
                                </div>
                                {submitError && <div className='text-red-500 text-sm font-semibold'>{SUBMIT_ERROR_MESSAGE}</div>}
                                <div className='mt-4'>
                                    <SmallButton className='blue-button w-full tracking-wide transition-colors duration-200 transform rounded-md' type='submit'>
                                        Submit
                                    </SmallButton>
                                </div>
                            </form>
                        )}
                        <div></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SummerSchoolSignUp;
